/**
 * Timezone Utility Functions for Tournament Times
 *
 * Handles conversion between local time, UTC, and display in CST/EST/PST
 */

export type TimezoneOption = "CST" | "EST" | "PST" | "local";

export const TIMEZONE_OFFSETS = {
  CST: -6, // UTC-6 (Central Standard Time)
  EST: -5, // UTC-5 (Eastern Standard Time)
  PST: -8, // UTC-8 (Pacific Standard Time)
} as const;

export const TIMEZONE_LABELS = {
  CST: "Central Time (CST)",
  EST: "Eastern Time (EST)",
  PST: "Pacific Time (PST)",
  local: "Local Time",
} as const;

/**
 * Convert datetime-local input value to UTC ISO string for database storage
 * @param localDateTimeString - Value from datetime-local input (YYYY-MM-DDTHH:mm)
 * @returns UTC ISO string for database
 */
export function convertLocalToUTC(localDateTimeString: string): string {
  if (!localDateTimeString) return "";

  // Create date object from local datetime string
  // The browser treats this as local time
  const localDate = new Date(localDateTimeString);

  // Convert to UTC ISO string for database
  return localDate.toISOString();
}

/**
 * Convert UTC ISO string to datetime-local input value
 * @param utcISOString - UTC ISO string from database
 * @returns Local datetime string for datetime-local input (YYYY-MM-DDTHH:mm)
 */
export function convertUTCToLocal(utcISOString: string): string {
  if (!utcISOString) return "";

  const date = new Date(utcISOString);

  // Format as YYYY-MM-DDTHH:mm for datetime-local input
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

/**
 * Format UTC date for display in specified timezone
 * @param utcISOString - UTC ISO string from database
 * @param timezone - Timezone to display in (CST/EST/PST/local)
 * @param includeSeconds - Whether to include seconds in output
 * @returns Formatted date string
 */
export function formatDateInTimezone(
  utcISOString: string,
  timezone: TimezoneOption = "CST",
  includeSeconds: boolean = false,
): string {
  if (!utcISOString) return "";

  const date = new Date(utcISOString);

  if (timezone === "local") {
    // Use browser's local timezone
    return formatLocalDate(date, includeSeconds);
  }

  // Convert to specified timezone
  const offset = TIMEZONE_OFFSETS[timezone];
  const timezoneDate = new Date(date.getTime() + offset * 60 * 60 * 1000);

  // Format: "Nov 26, 2:03 PM CST"
  const options: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    ...(includeSeconds && { second: "2-digit" }),
    hour12: true,
    timeZone: "UTC", // We've already adjusted for timezone
  };

  const formatted = timezoneDate.toLocaleString("en-US", options);

  // Add timezone abbreviation
  return `${formatted} ${timezone}`;
}

/**
 * Format date in browser's local timezone
 */
function formatLocalDate(date: Date, includeSeconds: boolean = false): string {
  const options: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    ...(includeSeconds && { second: "2-digit" }),
    hour12: true,
  };

  return date.toLocaleString("en-US", options);
}

/**
 * Format date range for tournament display
 * @param startTime - Tournament start time (UTC ISO string)
 * @param endTime - Tournament end time (UTC ISO string)
 * @param timezone - Timezone to display in
 * @returns Formatted date range string
 */
export function formatTournamentDateRange(
  startTime: string,
  endTime: string,
  timezone: TimezoneOption = "CST",
): string {
  const start = formatDateInTimezone(startTime, timezone);
  const end = formatDateInTimezone(endTime, timezone);

  return `${start} - ${end}`;
}

/**
 * Get current time in datetime-local format for input default values
 * @returns Current local time as YYYY-MM-DDTHH:mm
 */
export function getCurrentLocalDateTime(): string {
  const now = new Date();
  return convertUTCToLocal(now.toISOString());
}

/**
 * Get time in future for datetime-local input
 * @param hoursFromNow - Hours in the future
 * @returns Future local time as YYYY-MM-DDTHH:mm
 */
export function getFutureLocalDateTime(hoursFromNow: number): string {
  const future = new Date();
  future.setHours(future.getHours() + hoursFromNow);
  return convertUTCToLocal(future.toISOString());
}

/**
 * Calculate time remaining until tournament end
 * @param endTime - Tournament end time (UTC ISO string)
 * @returns Time remaining object with days, hours, minutes
 */
export function getTimeRemaining(endTime: string): {
  total: number;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isExpired: boolean;
} {
  const end = new Date(endTime).getTime();
  const now = Date.now();
  const total = end - now;

  if (total <= 0) {
    return {
      total: 0,
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      isExpired: true,
    };
  }

  const seconds = Math.floor((total / 1000) % 60);
  const minutes = Math.floor((total / 1000 / 60) % 60);
  const hours = Math.floor((total / (1000 * 60 * 60)) % 24);
  const days = Math.floor(total / (1000 * 60 * 60 * 24));

  return {
    total,
    days,
    hours,
    minutes,
    seconds,
    isExpired: false,
  };
}

/**
 * Format time remaining as human-readable string
 * @param endTime - Tournament end time (UTC ISO string)
 * @returns Human-readable time remaining (e.g., "2h 15m", "3d 5h")
 */
export function formatTimeRemaining(endTime: string): string {
  const remaining = getTimeRemaining(endTime);

  if (remaining.isExpired) {
    return "Ended";
  }

  if (remaining.days > 0) {
    return `${remaining.days}d ${remaining.hours}h`;
  }

  if (remaining.hours > 0) {
    return `${remaining.hours}h ${remaining.minutes}m`;
  }

  return `${remaining.minutes}m`;
}
